import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material';
import type { DeliveryQueueFailureReason, DeliveryQueueItem } from '../services/firebaseFunctions';
import { groupDeliveryQueueItems, type DeliveryQueueGroup } from '../utils/groupDeliveryQueueItems';
import { maskCpf } from '../utils/maskCpf';

const FAILURE_LABELS: Record<DeliveryQueueFailureReason, { label: string; color: 'error' | 'warning' | 'info' }> = {
  list_not_configured: { label: 'Lista não configurada', color: 'error' },
  list_misconfigured: { label: 'Lista mal configurada', color: 'warning' },
  list_exhausted: { label: 'Lista esgotada', color: 'info' },
};

function formatDate(iso: string): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const GROUP_COLUMNS = 7;

interface DeliveryQueueGroupRowProps {
  group: DeliveryQueueGroup;
  expanded: boolean;
  onToggle: () => void;
}

const DeliveryQueueGroupRow: React.FC<DeliveryQueueGroupRowProps> = ({ group, expanded, onToggle }) => (
  <>
    <TableRow hover sx={{ '& > td': { borderBottom: 'unset' } }}>
      <TableCell padding="checkbox">
        <IconButton
          size="small"
          onClick={onToggle}
          aria-label={expanded ? 'Recolher pendências' : 'Expandir pendências'}
        >
          {expanded ? <ArrowUpIcon /> : <ArrowDownIcon />}
        </IconButton>
      </TableCell>
      <TableCell>{group.companyName ?? group.companyId}</TableCell>
      <TableCell>{group.productName ?? group.productId}</TableCell>
      <TableCell>{group.productType ?? '-'}</TableCell>
      <TableCell align="center">
        <Typography component="span" fontWeight={700}>
          {group.minRequired}
        </Typography>
      </TableCell>
      <TableCell align="center">{group.itemCount}</TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {group.failureReasons.map((reason) => (
            <Chip
              key={reason}
              label={FAILURE_LABELS[reason].label}
              color={FAILURE_LABELS[reason].color}
              size="small"
            />
          ))}
        </Box>
      </TableCell>
    </TableRow>
    <TableRow>
      <TableCell colSpan={GROUP_COLUMNS} sx={{ py: 0 }}>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ my: 1.5, ml: 6 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>CPF</TableCell>
                  <TableCell align="center">Pendente</TableCell>
                  <TableCell align="center">Entregue</TableCell>
                  <TableCell>Motivo</TableCell>
                  <TableCell>Venda EVO</TableCell>
                  <TableCell>Criado em</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {group.items.map((item) => {
                  const failure = FAILURE_LABELS[item.failureReason];
                  return (
                    <TableRow key={item.id} hover>
                      <TableCell>{maskCpf(item.cpf)}</TableCell>
                      <TableCell align="center">{item.quantityPending}</TableCell>
                      <TableCell align="center">{item.quantityDelivered}</TableCell>
                      <TableCell>
                        <Chip label={failure.label} color={failure.color} size="small" />
                      </TableCell>
                      <TableCell>{item.evoSaleId}</TableCell>
                      <TableCell>{formatDate(item.createdAt)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  </>
);

interface DeliveryQueueTableProps {
  items: DeliveryQueueItem[];
}

const DeliveryQueueTable: React.FC<DeliveryQueueTableProps> = ({ items }) => {
  const groups = useMemo(() => groupDeliveryQueueItems(items), [items]);
  const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>({});

  const toggle = (key: string) =>
    setExpandedKeys((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" />
            <TableCell>Empresa</TableCell>
            <TableCell>Produto</TableCell>
            <TableCell>Tipo</TableCell>
            <TableCell align="center">Mín. necessário</TableCell>
            <TableCell align="center">Pendências</TableCell>
            <TableCell>Motivos</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((group) => (
            <DeliveryQueueGroupRow
              key={group.key}
              group={group}
              expanded={Boolean(expandedKeys[group.key])}
              onToggle={() => toggle(group.key)}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default DeliveryQueueTable;
